import { motion } from "framer-motion";
import { MessageSquareWarning, FileText, Users, HandHeart, Building2, Globe, Scale, PieChart, HeartPulse, Shield, Briefcase, ExternalLink, ArrowRight, ArrowLeft, FileSignature, Megaphone, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import { useCmsSection } from "@/hooks/useCmsSection";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTransparenceStore } from "@/data/transparenceStore";

const IconMap: Record<string, any> = {
  Users, FileSignature, Inbox, Megaphone, FileText, HandHeart, Building2, Globe, Scale, PieChart, HeartPulse, Shield, Briefcase, ExternalLink
};

const ActionsCitoyennesSection = () => {
  const { lang, isRtl } = useI18n();
  const cms = useCmsSection("actions");
  const { links: storedLinks, fetchLinks } = useTransparenceStore();

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);
  
  const title = lang === 'ar' ? "المنصات والعمل المواطن" : (lang === 'zgh' ? "ⵉⵏⵙⴰⵏ ⴷ ⵜⵉⵣⵎⵎⴰⵔ ⵏ ⵓⵏⴰⵎⵓⵔ" : "Plateformes et Actions Citoyennes");
  const tag = lang === 'ar' ? "الشفافية" : lang === 'zgh' ? "ⵜⴰⴼⴰⵡⵜ" : "Transparence";
  const desc = lang === 'ar' 
    ? "مؤسسات ومنصات وطنية رهن إشارتكم لتعزيز الشفافية والمشاركة في تدبير الشأن العام."
    : lang === 'zgh'
    ? "ⵜⵉⵎⵔⵙⴰⵍ ⴷ ⵜⵉⵏⵙⴰ ⵜⵉⵏⴰⵎⵓⵔⵉⵏ ⵉⵍⵍⴰⵏ ⴷⴰⵔⵓⵏ ⵃⵎⴰ ⴰⴷ ⵜⵙⴷⵓⵙⵎ ⵜⴰⴼⴰⵡⵜ ⴷ ⵓⴷⵔⴰⵡ ⴳ ⵓⵙⵡⵓⴷⴷⵓ ⴰⵣⴰⴳⵣ."
    : "L'action citoyenne à portée de clic : découvrez certaines plateformes au service du citoyen... et le chantier continue !";

  const mappedLinks = storedLinks.map(l => ({
    title: l.title[lang === 'ar' ? 'ar' : lang === 'zgh' ? 'zgh' : 'fr'] || l.title.fr,
    desc: l.desc[lang === 'ar' ? 'ar' : lang === 'zgh' ? 'zgh' : 'fr'] || l.desc.fr,
    url: l.url,
    icon: IconMap[l.iconName] || ExternalLink,
    imageUrl: l.imageUrl,
    internal: l.internal
  }));

  return (
    <section className="py-16 bg-card relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent/5 rounded-full blur-3xl -z-10" />

      <div className="container relative z-10">
        <motion.div
          className="text-center mb-16 max-w-4xl mx-auto flex flex-col items-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-secondary/10 text-secondary font-body text-base md:text-lg font-extrabold mb-1.5 border border-secondary/20 animate-pulse tracking-widest uppercase">
            {tag}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-extrabold text-foreground tracking-tight md:whitespace-nowrap">
            {cms[`title_${lang}`] ? cms[`title_${lang}`] : <span className="text-gradient-warm">{title}</span>}
          </h2>
          <p className="mt-1.5 text-muted-foreground font-body mx-auto text-lg leading-relaxed mb-2">
            {cms[`subtitle_${lang}`] ? cms[`subtitle_${lang}`] : desc}
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 max-w-6xl mx-auto">
          {mappedLinks.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={i}
                className="group p-4 md:p-6 rounded-2xl bg-background/60 backdrop-blur-sm border border-border/50 hover:border-primary/30 shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden flex flex-col h-full"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <div className="absolute top-0 left-0 w-1 h-full bg-primary/20 group-hover:bg-primary transition-colors" />
                
                <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4 mb-4 flex-1">
                  {item.imageUrl ? (
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl flex-shrink-0 flex items-center justify-center p-1.5 md:p-2 bg-white/80 group-hover:bg-white shadow-sm transition-all duration-300">
                      <img src={item.imageUrl} alt={item.title} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary/10 flex-shrink-0 flex items-center justify-center group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-300">
                      <Icon className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    {item.internal ? (
                      <Link to={item.url} className="block outline-none focus-visible:ring-2 rounded focus-visible:ring-primary">
                        <h3 className="text-base md:text-lg font-display font-semibold text-foreground mb-1.5 md:mb-2 group-hover:text-primary transition-colors leading-tight flex items-start gap-2">
                          <span className="truncate whitespace-normal line-clamp-2 pb-0.5">{item.title}</span>
                          <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity mt-1 flex-shrink-0 hidden sm:block" />
                        </h3>
                      </Link>
                    ) : (
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="block outline-none focus-visible:ring-2 rounded focus-visible:ring-primary">
                        <h3 className="text-base md:text-lg font-display font-semibold text-foreground mb-1.5 md:mb-2 group-hover:text-primary transition-colors leading-tight flex items-start gap-2">
                          <span className="truncate whitespace-normal line-clamp-2 pb-0.5">{item.title}</span>
                          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity mt-1 flex-shrink-0 hidden sm:block" />
                        </h3>
                      </a>
                    )}
                    <p className="text-muted-foreground font-body text-xs md:text-sm leading-relaxed line-clamp-3 md:line-clamp-none">{item.desc}</p>
                  </div>
                </div>
                
                <div className="mt-auto sm:ms-[3.25rem] md:ms-20">
                  <Button variant="link" className="p-0 h-auto text-secondary hover:text-secondary/80 font-body font-semibold text-sm group-hover:gap-2 transition-all w-fit" asChild>
                    {item.internal ? (
                      <Link to={item.url}>
                        {lang === 'ar' ? "قراءة المزيد" : (lang === 'zgh' ? "ⵖⵔ ⵓⴳⴳⴰⵔ" : "En savoir plus")} 
                        {isRtl ? <ArrowLeft className="w-3 h-3 ms-1" /> : <ArrowRight className="w-3 h-3 ms-1" />}
                      </Link>
                    ) : (
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        {lang === 'ar' ? "الولوج للمنصة" : (lang === 'zgh' ? "ⴽⵛⵎ ⵙ ⵓⵙⵉⵜ" : "Accéder au portail")} 
                        {isRtl ? <ArrowLeft className="w-3 h-3 ms-1" /> : <ArrowRight className="w-3 h-3 ms-1" />}
                      </a>
                    )}
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ActionsCitoyennesSection;

